class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;
    this.closeTimeout = null;
    this.isDrawerOpen = false;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);

    this.setHeaderCartIconAccessibility();
    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => {
      if (this.isDrawerOpen && event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });
  }

  disconnectedCallback() {
    this.stopFocusManagement();
    this.clearCloseTimeout();
    document.documentElement.classList.remove("prevent-scroll", "tf-cart-drawer-open");
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.setAttribute("aria-controls", this.id);
      cartLink.setAttribute("aria-expanded", "false");
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
      cartLink.addEventListener("keydown", (event) => {
        if (MinimogSettings.enable_cart_drawer && [" ", "Spacebar"].includes(event.key)) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    const wasOpen = this.isDrawerOpen;
    this.clearCloseTimeout();

    if (!wasOpen) {
      const focusOrigin = triggeredBy || document.activeElement;
      if (
        focusOrigin instanceof HTMLElement &&
        focusOrigin !== document.body &&
        focusOrigin !== document.documentElement &&
        !this.contains(focusOrigin)
      ) {
        this.setActiveElement(focusOrigin);
      }
    }

    this.isDrawerOpen = true;
    this.setAttribute("aria-hidden", "false");
    this.classList.add("m-cart-drawer--active");
    document.documentElement.classList.add("prevent-scroll", "tf-cart-drawer-open");
    this.updateTriggerState(true);
    this.startFocusManagement();

    requestAnimationFrame(() => {
      if (!this.isDrawerOpen) return;
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
      if (!wasOpen || !this.cartDrawerInner.contains(document.activeElement)) {
        this.focusInitialElement();
      }
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
  }

  close() {
    if (!this.isDrawerOpen) return;

    this.isDrawerOpen = false;
    this.stopFocusManagement();
    this.updateTriggerState(false);
    this.restoreFocus();
    this.setAttribute("aria-hidden", "true");
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");

    this.clearCloseTimeout();
    this.closeTimeout = window.setTimeout(() => {
      this.closeTimeout = null;
      if (this.isDrawerOpen) return;
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll", "tf-cart-drawer-open");
    }, 300);
  }

  clearCloseTimeout() {
    if (this.closeTimeout === null) return;
    window.clearTimeout(this.closeTimeout);
    this.closeTimeout = null;
  }

  startFocusManagement() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("focusin", this.handleFocusIn);
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("focusin", this.handleFocusIn);
  }

  stopFocusManagement() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("focusin", this.handleFocusIn);
  }

  handleKeydown(event) {
    if (!this.isDrawerOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    if (event.key !== "Tab") return;

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      this.cartDrawerInner.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (!this.cartDrawerInner.contains(activeElement)) {
      event.preventDefault();
      (event.shiftKey ? lastElement : firstElement).focus({ preventScroll: true });
    } else if (event.shiftKey && (activeElement === firstElement || activeElement === this.cartDrawerInner)) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  }

  handleFocusIn(event) {
    if (!this.isDrawerOpen || this.cartDrawerInner.contains(event.target)) return;
    this.focusInitialElement();
  }

  getFocusableElements() {
    const selector = [
      "a[href]",
      "area[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "iframe",
      "object",
      "embed",
      "details > summary:first-of-type",
      "[contenteditable]:not([contenteditable='false'])",
      "[tabindex]:not([tabindex^='-'])",
    ].join(",");

    return [...this.cartDrawerInner.querySelectorAll(selector)].filter((element) => {
      return (
        !element.closest("[hidden], [aria-hidden='true'], [inert]") &&
        element.getClientRects().length > 0 &&
        window.getComputedStyle(element).visibility !== "hidden"
      );
    });
  }

  focusInitialElement() {
    const firstElement = this.getFocusableElements()[0] || this.cartDrawerInner;
    firstElement.focus({ preventScroll: true });
  }

  restoreFocus() {
    const fallbackTrigger = [...document.querySelectorAll(".m-cart-icon-bubble")].find(
      (element) => element.getClientRects().length > 0
    );
    const focusTarget =
      this.activeElement instanceof HTMLElement && this.activeElement.isConnected
        ? this.activeElement
        : fallbackTrigger;

    this.activeElement = null;
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  updateTriggerState(isExpanded) {
    document.querySelectorAll(".m-cart-icon-bubble").forEach((cartLink) => {
      const isActiveTrigger = isExpanded && cartLink === this.activeElement;
      cartLink.setAttribute("aria-expanded", isActiveTrigger ? "true" : "false");
    });
  }

  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.open();
    });
    this.getCart()
      .then((cart) => this.updateCartCount(cart.item_count))
      .catch((error) => console.error(error));
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      cartCount.textContent = itemCount;
      if (itemCount > 0) {
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
    document.querySelectorAll(".m-cart-icon-bubble").forEach((cartLink) => {
      const label = cartLink.dataset.cartLabel || "Cart";
      cartLink.setAttribute("aria-label", `${label}: ${itemCount}`);
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (section.block === "cart-items") {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          } else {
            if (updateFooter) {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
            }
          }
        });
      })
      .catch((e) => {
        console.error(e);
      });
    this.getCart().then((cart) => {
      this.classList.toggle("m-cart--empty", cart.item_count === 0);
      this.updateCartCount(cart.item_count);
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems);
